import { Popover } from '@material-ui/core';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { SUB_PATHS } from '@/constants/urlConstants';
import { SearchableList, TextButton } from '@ds';
import { DSArrowDownIcon, DSPlusIcon } from '@ds-icons';
import {
  useModelViewCreateMutation,
  useModelViewRemoveMutation,
  useModelViewSetDefaultMutation,
  useModelViewUpdateMutation
} from '@mpm-druid/api';
import { ModelViewSummary } from '@mpm-druid/types';
import { generateEmptyView } from '@mpm-druid/utils';
import { usePopoverState } from '@shared/hooks';
import { useModelViewStore } from '../modelViewStore';
import { DashboardViewCreateModal } from './DashboardViewCreateModal';
import { DashboardViewSelectOption } from './DashboardViewSelectOption';

import styles from './DashboardViewSelect.module.scss';

type DashboardViewSelectProps = {
  views: ModelViewSummary[];
  selectedViewId: string | null;
  onSelectView: (viewId: string) => void;
};

export function DashboardViewSelect({
  views,
  selectedViewId,
  onSelectView
}: DashboardViewSelectProps) {
  const cancelAutoSave = useModelViewStore(state => state.cancelAutoSave);
  const flushAutoSave = useModelViewStore(state => state.flushAutoSave);

  const {
    mutateAsync: createModelViewAsync,
    isLoading: isCreatingView
  } = useModelViewCreateMutation();
  const {
    mutateAsync: updateModelViewAsync,
    isLoading: isUpdatingView
  } = useModelViewUpdateMutation();
  const { mutate: removeModelView } = useModelViewRemoveMutation();
  const {
    mutate: setDefaultModelView,
    mutateAsync: setDefaultModelViewAsync
  } = useModelViewSetDefaultMutation();

  const history = useHistory();
  const { modelId, workspace: workspaceName } = useParams<{
    modelId: string;
    workspace: string;
  }>();
  const {
    openPopover,
    onClosePopover,
    closePopover,
    isPopoverOpened,
    anchorElement
  } = usePopoverState();

  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);
  const [
    editingViewSummary,
    setEditingViewSummary
  ] = useState<ModelViewSummary | null>(null);

  const selectedView =
    views.find(view => view.id === selectedViewId) || views[0];

  const handleSelect = (viewId: string | null) => {
    if (!viewId) return;

    onSelectView(viewId);
    closePopover();
  };

  const handleSubmitCreateView = async (name: string, isDefault: boolean) => {
    try {
      flushAutoSave();

      const updatedView = editingViewSummary?.id
        ? await updateModelViewAsync({
            modelId,
            partialView: { name },
            viewId: editingViewSummary.id
          })
        : await createModelViewAsync({
            modelId,
            view: generateEmptyView(name)
          });

      if (isDefault) {
        await setDefaultModelViewAsync({ modelId, viewId: updatedView.id });
      }

      history.push(
        `/${workspaceName}/${SUB_PATHS.MODEL_PRODUCTION_MONITORING}/${modelId}/dashboard/${updatedView.id}`
      );
    } finally {
      setIsCreateViewOpen(false);
      setEditingViewSummary(null);
      closePopover();
    }
  };

  const handleSetDefaultView = (viewId: string | null) => {
    if (!viewId) return;

    setDefaultModelView({ modelId, viewId });
  };

  const handleDeleteView = (viewId: string | null) => {
    if (!viewId) return;

    if (viewId === selectedViewId) {
      cancelAutoSave();
    }

    removeModelView({ modelId, viewId });
  };

  return (
    <>
      <TextButton
        data-test="view-select-button"
        onClick={openPopover}
        active={isPopoverOpened}
        type="primary"
        size="medium"
        PostfixIcon={<DSArrowDownIcon />}
        className={styles.viewBtnContainer}
      >
        <span className={styles.viewBtnPrefix}>View:</span>
        {selectedView?.name}
      </TextButton>

      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          className: styles.viewSelectorContainer
        }}
        elevation={0}
        open={isPopoverOpened}
        anchorEl={anchorElement}
        onClose={onClosePopover}
      >
        <SearchableList itemList={views} searchField="name">
          <SearchableList.Result>
            {(viewSummary: ModelViewSummary) => (
              <DashboardViewSelectOption
                data={viewSummary}
                selectedViewId={selectedViewId}
                onClick={() => handleSelect(viewSummary.id)}
                onEditView={() => setEditingViewSummary(viewSummary)}
                onSetDefaultView={() => handleSetDefaultView(viewSummary.id)}
                onDeleteView={() => handleDeleteView(viewSummary.id)}
              />
            )}
          </SearchableList.Result>
          <SearchableList.NotFound />
          <SearchableList.Footer>
            <SearchableList.FooterActionLink
              text="Create new view"
              to="#"
              onClick={() => setIsCreateViewOpen(true)}
              PrefixIcon={<DSPlusIcon />}
            />
          </SearchableList.Footer>
        </SearchableList>
      </Popover>

      {isCreateViewOpen || editingViewSummary ? (
        <DashboardViewCreateModal
          isSubmitting={isCreatingView || isUpdatingView}
          onClose={() => {
            setIsCreateViewOpen(false);
            setEditingViewSummary(null);
          }}
          onSubmit={handleSubmitCreateView}
          viewSummary={editingViewSummary}
        />
      ) : null}
    </>
  );
}
